/**
 * 扑克行话中文化（仅显示层）。
 * 原则：只规范化展示给用户的文本，绝不改动数据/parser/金标所用的原始字段。
 * 术语表保守——只收录高置信、词边界清晰、新手必懵的行话，避免破坏教学内容。
 *
 * 规则顺序铁律：复合词（含连字符/空格）在前，短词在后；
 * 否则短词会先吃掉复合词的一部分（如 bet 吃掉 c-bet 的尾巴）。
 * 所有短词必须带 \b 词边界，防止切坏中文语境里的英文片段。
 */

const JARGON_RULES: Array<[RegExp, string]> = [
  // ── 角色 ──
  [/\bHero\b/gi, "你"],
  [/\bVillain\b/gi, "对手"],

  // ── 位置缩写（大写精确匹配 + 词边界）──
  [/\bUTG\+1\b/g, "枪口+1"],
  [/\bUTG\b/g, "枪口位"],
  [/\bLJ\b/g, "劫机位"],
  [/\bHJ\b/g, "劫持位"],
  [/\bHijack\b/gi, "劫持位"],
  [/\bMP\b/g, "中位"],
  [/\bCO\b/g, "关煞位"],
  [/\bBTN\b/gi, "庄位"],
  // 按钮位消歧(D'):位置名 button 与屏幕按钮撞名,P1/P3 实测双双误读 → 统一显示「庄家位(BTN)」
  [/按钮位/g, "庄家位(BTN)"],
  [/按钮/g, "庄家位(BTN)"],
  [/\bButton\b/gi, "庄家位(BTN)"],
  [/\bSB\b/g, "小盲"],
  [/\bBB\b/g, "大盲"],
  [/\bEP\b/g, "前位"],
  [/\bLP\b/g, "后位"],

  // ── 复合动作（长→短，必须先于单动作词）──
  [/\bcheck-raise\b/gi, "过牌加注"],
  [/\bcheck-call\b/gi, "过牌跟注"],
  [/\bcheck-back\b/gi, "过牌回让"],
  [/\bbet-fold\b/gi, "下注弃牌"],
  [/\bbet-call\b/gi, "下注跟注"],
  [/\bdelayed[ -]?c-?bet\b/gi, "延迟持续下注"],
  [/\bc-?bet\b/gi, "持续下注"],
  [/\b3-?bet\b/gi, "再加注"],
  [/\b4-?bet\b/gi, "四加注"],
  [/\b5-?bet\b/gi, "五加注"],
  [/\ball-?in\b/gi, "全下"],
  [/\bsemi-bluff\b/gi, "半诈唬"],
  [/\bvalue-?bet\b/gi, "价值下注"],
  [/\bprobe-?bet\b/gi, "试探下注"],
  [/\bblocker-?bet\b/gi, "阻隔下注"],
  [/\boverbet\b/gi, "超池下注"],
  [/\bdouble-?barrel\b/gi, "双管齐下"],
  [/\btriple-?barrel\b/gi, "三管齐下"],

  // ── 复合概念（长→短）──
  [/\btop[ -]?pair[ -]?top[ -]?kicker\b/gi, "顶对顶踢"],
  [/\btop[ -]?pair\b/gi, "顶对"],
  [/\boverpair\b/gi, "超对"],
  [/\bflush[ -]?draw\b/gi, "同花听牌"],
  [/\bstraight[ -]?draw\b/gi, "顺子听牌"],
  [/\bgut[ -]?shot\b/gi, "卡顺听牌"],
  [/\bopen[ -]?ended\b/gi, "两头顺听牌"],
  [/\bcombo[ -]?draw\b/gi, "组合听牌"],
  [/\bfold[ -]?equity\b/gi, "弃牌权益"],
  [/\bpot[ -]?odds\b/gi, "底池赔率"],
  [/\bpot[ -]?control\b/gi, "底池控制"],
  [/\bpot[ -]?commit\b/gi, "底池承诺"],
  [/\bimplied[ -]?odds\b/gi, "隐含赔率"],
  [/\bboard[ -]?texture\b/gi, "牌面结构"],
  [/\bdry[ -]?board\b/gi, "干燥牌面"],
  [/\bwet[ -]?board\b/gi, "潮湿牌面"],
  [/\bstack[ -]?to[ -]?pot\b/gi, "筹码底池比"],
  [/\bthin[ -]?value\b/gi, "薄价值"],
  [/\bslow[ -]?play\b/gi, "慢打"],
  [/\bsqueeze[ -]?play\b/gi, "挤压打法"],

  // ── 单词动作（高频短词，\b 必须）──
  [/\bcall\b/gi, "跟注"],
  [/\bfold\b/gi, "弃牌"],
  [/\bcheck\b/gi, "过牌"],
  [/\bbet\b/gi, "下注"],
  [/\braise\b/gi, "加注"],
  [/\blimp\b/gi, "平跟入池"],
  [/\bdonk\b/gi, "领打"],

  // ── 单词概念 ──
  [/\bbluff\b/gi, "诈唬"],
  [/\bequity\b/gi, "胜率权益"],
  [/\brange\b/gi, "手牌范围"],
  [/\bblockers?\b/gi, "阻隔牌"],
  [/\bkicker\b/gi, "踢脚"],
  [/\bdraw\b/gi, "听牌"],
  [/\bset\b/gi, "暗三条"],
  [/\btrips\b/gi, "明三条"],
  [/\bnuts\b/gi, "坚果牌"],
  [/\bquads\b/gi, "四条"],
  [/\bpolarized\b/gi, "两极化范围"],
  [/\bmerged\b/gi, "融合范围"],
  [/\bsqueeze\b/gi, "挤压加注"],
  [/\bisolation\b/gi, "隔离加注"],
  [/\bresteal\b/gi, "反偷盲"],
  [/\bsteal\b/gi, "偷盲"],
  [/\bbackdoor\b/gi, "后门听牌"],
  [/\bSPR\b/gi, "筹码底池比"],
  [/\bOOP\b/g, "不利位置"],
  [/\bIP\b/g, "有利位置"],

  // ── 底池（放在所有 pot-* 复合词之后）──
  [/\bpot\b/gi, "底池"],
];

export function jargonZh(text: string | null | undefined): string {
  if (!text) return "";
  let out = text;
  for (const [re, zh] of JARGON_RULES) {
    out = out.replace(re, zh);
  }
  return out;
}

// ── 答文变体(/coach 答文体显示层翻译 · r3 评审 learner_surface_purity 9→10)──
// r2 取证:coach 历史答文体裸英文术语串 ~25 命中(BTN/BB/open/call/3bet/4bet/cbet/XR/overfold)。
// 主表已覆盖 BTN/BB/call/3bet/4bet/cbet;本变体补齐 open/XR/overfold 等裸词。
// 与 jargonZh 的两点差异:
//  1. 剔除「按钮/按钮位→庄家位(BTN)」场景特化中文重写——教练答文里「点跟注按钮」指 UI 按钮,
//     重写会把日常散文切坏(该规则只服务于场景标题/行动线的按钮位消歧);
//  2. 追加散文裸词(open/XR/overfold)。主表既有测试锁定 jargonZh 不译 open,故只进本变体。
// 铁律不变:只动显示层,存储数据零改动;未登录词静默原样,不编造。
const JARGON_PROSE_EXCLUDED_SOURCES = new Set(["按钮位", "按钮"]);

// 长→短铁律的两侧夹击:open-raise 必须先于主表 \braise\b(否则 raise 吃掉尾巴),
// 裸 open 必须后于主表 \bopen[ -]?ended\b(否则 open 切碎 open-ended)。
const JARGON_PROSE_PRE: Array<[RegExp, string]> = [
  [/\bopen[ -]?raise\b/gi, "开池加注"],
];

const JARGON_PROSE_POST: Array<[RegExp, string]> = [
  [/\bopen\b/gi, "开池"],
  [/X\/R(?![A-Za-z0-9])/g, "过牌加注"],
  [/\bXR\b/g, "过牌加注"],
  [/\boverfold\b/gi, "过度弃牌"],
];

const JARGON_PROSE_RULES: Array<[RegExp, string]> = [
  ...JARGON_PROSE_PRE,
  ...JARGON_RULES.filter(([re]) => !JARGON_PROSE_EXCLUDED_SOURCES.has(re.source)),
  ...JARGON_PROSE_POST,
];

/**
 * /coach 答文体显示层翻译(仅显示层,存储数据零改动;未登录词静默原样不编造)。
 * 先例 = positionZh/streetZh 显示层翻译:r2 取证词集 BTN/BB/open/call/3bet/4bet/cbet/XR/overfold → 中文。
 */
export function jargonZhProse(text: string | null | undefined): string {
  if (!text) return "";
  let out = text;
  for (const [re, zh] of JARGON_PROSE_RULES) {
    out = out.replace(re, zh);
  }
  return out;
}
